import React, { forwardRef, MutableRefObject } from 'react';
import { Box, Input, Textarea } from '../../Rebass';

import styled from '@emotion/styled';
import Text from '../../Text';
import { inputStyle } from '../styles';
import theme from '../../../theme';
import { css } from '@emotion/react';
import Icon from '../../Icon';
import { SxStyleProp } from '../../Rebass/types';

type Variant = 'standard' | 'outline';
type Size = 'standard' | 'large';
interface TextInputProps {
  name?: string;
  label?: string;
  placeholder?: string;
  error?: string;
  type: 'input' | 'textarea';
  variant: Variant;
  sx?: SxStyleProp;
  icon?: { onClick?: () => void; name?: string; sx?: SxStyleProp };
  size: Size;
  color?: string;
  bg?: string;
  onChange?: any;
  value?: any;
  disabled?: boolean;
  readOnly?: boolean;
}

interface InputProps {
  error?: string;
  variant: Variant;
  color?: string;
  bg?: string;
}

const styles = {
  standard: '',
  outline: css`
    background: transparent;
    border: none;
    border-bottom: 1px solid ${theme.colors.baseGrayLight};
    border-radius: 0;
  `,
};

const errorStyles = {
  standard: css`
    border: 1px solid ${theme.colors.statusError};
  `,
  outline: css`
    border-bottom: 1px solid ${theme.colors.statusError};
  `,
};

const StyledInput = styled(Input)<InputProps>`
  ${inputStyle}
  ${({ variant }) => styles[variant]}
  ${({ color }) =>
    color
      ? `
        color: ${theme.colors[color]};
        &::placeholder {
          color: ${theme.colors[color]};
          opacity: 0.6;
        }`
      : ''}
      ${({ error, variant }) => (error ? errorStyles[variant] : '')}
`;
const StyledTextArea = styled(Textarea)<InputProps>`
  ${inputStyle}
  ${({ variant }) => styles[variant]}
  ${({ error, variant }) => (error ? errorStyles[variant] : '')}
`;

const inputMap = {
  input: StyledInput,
  textarea: StyledTextArea,
};

const TextInput = forwardRef(
  (
    {
      name,
      label,
      error,
      type = 'input',
      variant = 'standard',
      sx,
      icon,
      size,
      bg,
      onChange,
      placeholder,
      value,
      disabled,
      readOnly,
      ...input
    }: TextInputProps,
    ref: MutableRefObject<any>,
  ) => {
    const InputComponent = inputMap[type];

    return (
      <Box sx={sx}>
        {label && (
          <Box>
            <Text as="label" htmlFor={name} size="small" fontWeight="bold">
              {label}
            </Text>
          </Box>
        )}
        <Box sx={{ position: 'relative' }} mb={error ? [1] : [0]}>
          <InputComponent
            ref={ref}
            id={name}
            name={name}
            error={error}
            variant={variant}
            fontSize={size === 'large' ? ['menuM', 'menu'] : 'small'}
            fontFamily={size === 'large' ? 'serif' : 'sans'}
            fontWeight={size === 'large' ? 'normal' : 'light'}
            bg={bg}
            aria-label={label}
            disabled={disabled}
            readOnly={readOnly}
            {...input}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
          />
          {icon && (
            <Box
              sx={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
              }}
            >
              <Icon
                size={16}
                {...icon}
                sx={{ color: 'textDark', ...icon.sx }}
              />
            </Box>
          )}
        </Box>
        {error && (
          <Text color="statusError" size="small">
            {error}
          </Text>
        )}
      </Box>
    );
  },
);

export default TextInput;
